import Banner from '@zenbusiness/zui/Banner';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router-dom';

import { ORDER_TASK_STATUS } from '#client/constants/application';
import { DATE_FORMAT } from '#client/constants/date';
import { MISSING_PAYMENT_EXPIRED_DAY, SUBSCRIPTION_STATUS } from '#client/constants/subscription';
import useBillingPath from '#client/hooks/useBillingPath';
import useOrderTaskContext from '#client/hooks/useOrderTaskContext';
import useSubScriptionContext from '#client/hooks/useSubscriptionContext';
import {
  defaultPayloadBannerDisplayed,
  defaultPayloadButtonPressed
} from '#client/hooks/useTrackEvent';
import { BannerWrapperStyled } from '#client/pages/SharedStyles';

dayjs.extend(utc);

export default function MissingPaymentBanner() {
  const navigate = useNavigate();
  const billingPath = useBillingPath();

  const { loading, error, missingPaymentDate, subscriptionStatus, isMissedPayment } =
    useSubScriptionContext();
  const { isLoadingOrderTask, bankingPreferredTask, orderTaskError } = useOrderTaskContext();

  const handleClick = () => {
    navigate(billingPath);
  };

  const isActiveBanking = bankingPreferredTask?.status === ORDER_TASK_STATUS.ACTIVE;
  const isShowMissingPaymentBanner =
    isActiveBanking && isMissedPayment && subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE;
  const makePaymentDate = dayjs(missingPaymentDate).add(MISSING_PAYMENT_EXPIRED_DAY, 'day');
  const makePaymentDateText = dayjs
    .utc(makePaymentDate ?? '')
    .local()
    .format(DATE_FORMAT.MOVE_MONEY);

  // When has error from api, return null
  if (error || orderTaskError) {
    return null;
  }

  // When api status is loading, return undefined
  if (loading || isLoadingOrderTask) {
    return undefined;
  }

  return isShowMissingPaymentBanner ? (
    <BannerWrapperStyled>
      <Banner
        clickstreamProps={{
          bannerDisplayed: {
            ...defaultPayloadBannerDisplayed,
            bannerType: 'info',
            appPageName: 'active_banking_dashboard',
            elementName: 'subscription_on_hold_banking'
          }
        }}
        slotProps={{
          primaryAction: {
            label: 'Make payment',
            clickstreamProps: {
              ...defaultPayloadButtonPressed,
              elementName: 'make_payment_from_banner',
              elementText: 'Make payment',
              appPageName: 'active_banking_dashboard'
            },
            onClick: handleClick
          }
        }}
        slots={{
          startIcon: (
            <SharedAsset
              alt=""
              color="#E53535"
              src="/s2/icon/warning-sign-fill.svg"
              width={34}
            />
          )
        }}
        subtitle={`To keep using ZenBusiness Banking, please make a payment. If you don’t make a payment by ${makePaymentDateText}, your account will be closed.`}
        title="Your subscription is on hold"
        variant="alert"
      />
    </BannerWrapperStyled>
  ) : null;
}
